import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { interestRepository } from '@/data/interests/repositories/interest_repository_impl'
import type { Interest } from '@/domain/interests/entities/interest.entity'
import { ListInterestsUseCase } from '@/domain/interests/usecases/list_interests.usecase'
import { UpdateInterestUseCase } from '@/domain/interests/usecases/update_interest.usecase'
import { getAxiosLikeErrorMessage } from '@/services/apiErrors'
import { logError } from '@/utils/logger'
import type { InterestFormErrors } from '../models/interests_ui_state'

const defaultListInterests = new ListInterestsUseCase(interestRepository)
const defaultUpdateInterest = new UpdateInterestUseCase(interestRepository)

export interface UseEditInterestViewModelDeps {
  listInterests?: ListInterestsUseCase
  updateInterest?: UpdateInterestUseCase
}

export function useEditInterestViewModel(deps: UseEditInterestViewModelDeps = {}) {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const listInterests = deps.listInterests ?? defaultListInterests
  const updateInterest = deps.updateInterest ?? defaultUpdateInterest
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [interest, setInterest] = useState<Interest | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [interestName, setInterestName] = useState('')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isActive, setIsActive] = useState(true)
  const [fileName, setFileName] = useState('No file chosen')
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [errors, setErrors] = useState<InterestFormErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const applyInterestToForm = useCallback((item: Interest) => {
    setInterest(item)
    setInterestName(item.name)
    setIsActive(item.status === 'active')
    if (item.imageUrl) {
      setImagePreview(item.imageUrl)
      setFileName('Current file')
    } else if (item.icon && item.icon.startsWith('http')) {
      setImagePreview(item.icon)
      setFileName('Current file')
    }
  }, [])

  useEffect(() => {
    if (!id) {
      setIsLoading(false)
      return
    }

    let cancelled = false

    const load = async () => {
      try {
        setIsLoading(true)
        const list = await listInterests.execute({ page: 0, limit: 50 })
        if (cancelled) return
        const found = list.find(item => item.id === id) ?? null
        if (found) {
          applyInterestToForm(found)
        } else {
          setInterest(null)
        }
      } catch (error: unknown) {
        logError('Failed to load interest', error)
        if (!cancelled) setInterest(null)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [applyInterestToForm, id, listInterests])

  const resetPreviewToOriginal = useCallback(() => {
    if (!interest) {
      setImagePreview(null)
      return
    }
    if (interest.imageUrl) {
      setImagePreview(interest.imageUrl)
    } else if (interest.icon && interest.icon.startsWith('http')) {
      setImagePreview(interest.icon)
    } else {
      setImagePreview(null)
    }
  }, [interest])

  const handleFileChange = useCallback(
    (file: File | undefined) => {
      if (file) {
        setSelectedFile(file)
        setFileName(file.name)
        setErrors(prev => ({ ...prev, icon: undefined }))
        const reader = new FileReader()
        reader.onloadend = () => {
          setImagePreview(reader.result as string)
        }
        reader.readAsDataURL(file)
      } else {
        setSelectedFile(null)
        setFileName('No file chosen')
        resetPreviewToOriginal()
      }
    },
    [resetPreviewToOriginal]
  )

  const removeSelectedFile = useCallback(() => {
    setSelectedFile(null)
    setFileName('No file chosen')
    resetPreviewToOriginal()
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [resetPreviewToOriginal])

  const submit = useCallback(async () => {
    setIsSubmitting(true)

    if (!id) {
      alert('Invalid interest ID')
      setIsSubmitting(false)
      return
    }

    const newErrors: InterestFormErrors = {}
    if (!interestName.trim()) {
      newErrors.name = 'Interest name is required'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      setIsSubmitting(false)
      return
    }

    try {
      if (selectedFile) {
        alert(
          'Image upload requires a hosted iconUrl. Enter a URL or upload via your CDN, then save.'
        )
        setIsSubmitting(false)
        return
      }

      await updateInterest.execute({ id, name: interestName.trim() })
      navigate('/admin/interest')
    } catch (error: unknown) {
      logError('Failed to update interest:', error)
      alert(getAxiosLikeErrorMessage(error, 'Failed to update interest. Please try again.'))
    } finally {
      setIsSubmitting(false)
    }
  }, [id, interestName, navigate, selectedFile, updateInterest])

  const cancel = useCallback(() => {
    navigate('/admin/interest')
  }, [navigate])

  return {
    fileInputRef,
    interest,
    isLoading,
    form: {
      interestName,
      selectedFile,
      isActive,
      fileName,
      imagePreview,
      errors,
      isSubmitting,
    },
    actions: {
      setInterestName,
      setIsActive,
      handleFileChange,
      removeSelectedFile,
      openFilePicker: () => fileInputRef.current?.click(),
      submit,
      cancel,
      clearFieldError: (field: keyof InterestFormErrors) =>
        setErrors(prev => ({ ...prev, [field]: undefined })),
    },
  }
}
